import { useMemo } from 'react';
import { useSimulatorStore } from '../../stores/useSimulatorStore';
import { calculateAllFeatureWTP } from '../../lib/calculations';
import { formatPrice } from '../../lib/formatters';
import { Crown, Layers, Tag } from 'lucide-react';
import { InfoButton } from '../common/Tooltip';

interface TierGroup {
  label: string;
  icon: typeof Crown;
  color: string;
  features: { name: string; wtp: number; lift: number }[];
}

export function FeatureAllocationPanel() {
  const selectedPrice = useSimulatorStore(s => s.selectedPrice);
  const assumptions = useSimulatorStore(s => s.assumptions);

  const tiers = useMemo((): TierGroup[] => {
    const wtps = calculateAllFeatureWTP(selectedPrice, assumptions);

    const premium = wtps.slice(0, 3).map(w => ({ name: w.name, wtp: w.impliedDollarValue, lift: w.lift }));
    const core = wtps.slice(3, 5).map(w => ({ name: w.name, wtp: w.impliedDollarValue, lift: w.lift }));
    const starter = wtps.slice(5).map(w => ({ name: w.name, wtp: w.impliedDollarValue, lift: w.lift }));

    return [
      { label: 'Premium', icon: Crown, color: '#14B8A6', features: premium },
      { label: 'Core', icon: Layers, color: '#3B82F6', features: core },
      { label: 'Starter', icon: Tag, color: '#F59E0B', features: starter },
    ];
  }, [selectedPrice, assumptions]);

  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-1">
        <h3 className="text-heading font-semibold text-secondary uppercase tracking-wider">Suggested Feature Allocation</h3>
        <InfoButton helpKey="featureAllocation" size={13} />
      </div>
      <p className="text-sm text-muted mb-4">
        Features ranked by implied WTP at {formatPrice(selectedPrice)} and allocated across three tiers. Adjust in the Package Builder.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {tiers.map(tier => {
          const Icon = tier.icon;
          return (
            <div key={tier.label} className="rounded-lg border p-4" style={{ borderColor: tier.color + '40' }}>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: tier.color + '15' }}>
                  <Icon size={14} style={{ color: tier.color }} />
                </div>
                <span className="text-sm font-semibold text-primary">{tier.label}</span>
              </div>
              <div className="space-y-2">
                {tier.features.map(f => (
                  <div key={f.name} className="flex items-center justify-between">
                    <span className="text-xs text-primary">{f.name}</span>
                    <div className="text-right">
                      <span className="text-xs font-mono font-semibold" style={{ color: tier.color }}>
                        {formatPrice(Math.round(f.wtp))}
                      </span>
                      <span className="text-micro text-muted font-mono ml-1">
                        +{(f.lift * 100).toFixed(1)}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-micro text-muted mt-3 italic leading-relaxed">
        Suggested allocation based on conjoint value analysis. Feature interactions were not measured — a feature's value may change depending on what other features are in the bundle. These suggestions assume independent feature effects.
      </p>
    </div>
  );
}
